import {
  Device,
  type HardwareShippingRates,
} from "@screencloud/billing-client-api";
import { CurrencyCode } from "../PlanBillingStep/constants";

/**
 * Hardware shipping rates come from the billing-client-api `hardwareshippingrates`
 * resource (added in SDK 5.3.0, screencloud/apps#13928). The fetch lives in
 * `service.client.ts` (`getHardwareShippingRates`); this module just re-exports
 * the SDK types and the pure selector used by the checkout.
 *
 * A rate row is keyed by country bucket + currency + quantity tier + service, so
 * the selector matches on the shipping country, the order currency and the screen
 * count (which selects the tier), preferring standard over express service.
 *
 * NOTE: the API returns `amount` in MAJOR currency units (e.g. 21 = $21.00),
 * whereas plan prices are in minor units (cents); `shippingAmountFor` converts to
 * cents so the two can be combined.
 */
export { Device };
export type ShippingRate = HardwareShippingRates.ShippingRate;

// Cheapest/most-common service first; buckets without standard fall back to express.
const SERVICE_PRIORITY = ["standard", "express"];

/** Whether a quantity tier ("1-5", "6-10", "11+") covers a given screen count. */
const tierCoversCount = (tier: string, count: number): boolean => {
  const open = /^(\d+)\+$/.exec(tier);
  if (open) return count >= Number(open[1]);
  const range = /^(\d+)-(\d+)$/.exec(tier);
  if (range) return count >= Number(range[1]) && count <= Number(range[2]);
  return false;
};

/** Whether a rate is within its effective-from/to window (open-ended when null). */
const isEffective = (rate: ShippingRate): boolean => {
  const now = Date.now();
  if (rate.effectiveFrom && now < Date.parse(rate.effectiveFrom)) return false;
  if (rate.effectiveTo && now > Date.parse(rate.effectiveTo)) return false;
  return true;
};

export interface ShippingRateQuery {
  countryBucket: string | undefined;
  currency: CurrencyCode;
  screenCount: number;
  /**
   * Shipping service the user picked (e.g. "standard" / "express"). When set it
   * is preferred; when unset the selector falls back to SERVICE_PRIORITY so the
   * legacy auto-select behavior is preserved.
   */
  shippingService?: string;
}

/** The active rate for a bucket + currency + quantity tier and preferred service. */
export const shippingRateFor = (
  rates: ShippingRate[],
  { countryBucket, currency, screenCount, shippingService }: ShippingRateQuery,
): ShippingRate | undefined => {
  const candidates = rates.filter(
    (rate) =>
      rate.active &&
      isEffective(rate) &&
      rate.countryBucket === countryBucket &&
      rate.currency.toUpperCase() === currency &&
      tierCoversCount(rate.quantityTier, screenCount),
  );
  // Try the picked service first, then fall back to the default priority order.
  const priority = shippingService
    ? [
        shippingService,
        ...SERVICE_PRIORITY.filter((s) => s !== shippingService),
      ]
    : SERVICE_PRIORITY;
  const byService = priority
    .map((wanted) => candidates.find((rate) => rate.service === wanted))
    .find(Boolean);
  return byService ?? candidates[0];
};

/** Shipping charge in MINOR units (cents) for the order, or 0 if no rate matches. */
export const shippingAmountFor = (
  rates: ShippingRate[],
  query: ShippingRateQuery,
): number => {
  const rate = shippingRateFor(rates, query);
  return rate ? rate.amount * 100 : 0;
};
